import { useEffect, useMemo, useState, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL || '';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DOMAIN_RE = /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/i;

const MODE_OPTIONS = [
  {
    value: 'local',
    label: 'PCG-managed only',
    blurb: 'Bcrypt password + PCG-issued MFA. The default for every new org.',
  },
  {
    value: 'federated',
    label: 'Microsoft Entra ID (federated)',
    blurb: 'Sign-in handled by the client’s own tenant. PCG never sees their password.',
  },
  {
    value: 'hybrid',
    label: 'Hybrid (both paths allowed)',
    blurb: 'Useful during migration. Most users sign in via Microsoft; a few legacy accounts keep passwords.',
  },
];

const JIT_ROLE_OPTIONS = [
  { value: 'associate', label: 'Associate' },
  { value: 'admin', label: 'Admin' },
  { value: 'superuser', label: 'Super User (org)' },
];

export default function FederationModal({ org, onClose, onSaved }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);

  const [mode, setMode] = useState('local');
  const [tenantId, setTenantId] = useState('');
  const [domainInput, setDomainInput] = useState('');
  const [domains, setDomains] = useState([]);
  const [jitEnabled, setJitEnabled] = useState(false);
  const [jitRole, setJitRole] = useState('associate');

  const [linkRecipientEmail, setLinkRecipientEmail] = useState('');
  const [linkRecipientName, setLinkRecipientName] = useState('');
  const [sendingLink, setSendingLink] = useState(false);
  const [linkSentTo, setLinkSentTo] = useState(null);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const res = await fetch(`${API}/api/admin/v2/orgs/${org.id}/federation`, { credentials: 'include' });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Failed to load');
      setConfig(data.federation);
      setMode(data.federation.identity_mode || 'local');
      setTenantId(data.federation.azure_tenant_id || '');
      setDomains(Array.isArray(data.federation.allowed_email_domains) ? data.federation.allowed_email_domains : []);
      setJitEnabled(!!data.federation.jit_provisioning_enabled);
      setJitRole(data.federation.jit_default_role || 'associate');
    } catch (e) {
      setErr(e.message);
    }
    setLoading(false);
  }, [org.id]);

  const fetchEvents = useCallback(async () => {
    setEventsLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/v2/orgs/${org.id}/federation/events?limit=20`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) setEvents(data.events || []);
    } catch {
      // non-fatal
    }
    setEventsLoading(false);
  }, [org.id]);

  useEffect(() => {
    fetchConfig();
    fetchEvents();
  }, [fetchConfig, fetchEvents]);

  const tenantValid = mode === 'local' || UUID_RE.test(tenantId.trim());
  const domainsValid = mode === 'local' || domains.length > 0;
  const canSave = !saving && tenantValid && domainsValid;

  const addDomain = () => {
    const d = domainInput.trim().toLowerCase();
    if (!d) return;
    if (!DOMAIN_RE.test(d)) {
      setErr(`Invalid domain: ${d}`);
      return;
    }
    if (domains.includes(d)) {
      setDomainInput('');
      return;
    }
    setDomains([...domains, d]);
    setDomainInput('');
    setErr(null);
  };

  const removeDomain = (d) => setDomains(domains.filter((x) => x !== d));

  const save = async () => {
    setErr(null);
    setSaving(true);
    try {
      const body = {
        identity_mode: mode,
        azure_tenant_id: mode === 'local' ? null : tenantId.trim().toLowerCase(),
        allowed_email_domains: mode === 'local' ? [] : domains,
        jit_provisioning_enabled: mode === 'local' ? false : jitEnabled,
        jit_default_role: mode === 'local' ? null : (jitEnabled ? jitRole : null),
      };
      const res = await fetch(`${API}/api/admin/v2/orgs/${org.id}/federation`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Save failed');
      onSaved?.({ org, mode });
      await fetchConfig();
      await fetchEvents();
    } catch (e) {
      setErr(e.message);
    }
    setSaving(false);
  };

  const sendSignInLink = async () => {
    setErr(null);
    setLinkSentTo(null);
    if (!linkRecipientEmail.includes('@')) {
      setErr('Enter a valid recipient email');
      return;
    }
    setSendingLink(true);
    try {
      const res = await fetch(`${API}/api/admin/v2/orgs/${org.id}/federation/send-signin-link`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          recipient_email: linkRecipientEmail.trim(),
          recipient_name: linkRecipientName.trim() || undefined,
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Send failed');
      setLinkSentTo(data.sent_to);
      await fetchEvents();
    } catch (e) {
      setErr(e.message);
    }
    setSendingLink(false);
  };

  const resetVerification = async () => {
    if (!window.confirm('Reset verification? The next sign-in will re-mark this organization Verified.')) return;
    setSaving(true);
    setErr(null);
    try {
      const res = await fetch(`${API}/api/admin/v2/orgs/${org.id}/federation/reset-verification`, {
        method: 'POST', credentials: 'include',
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Reset failed');
      await fetchConfig();
      await fetchEvents();
    } catch (e) {
      setErr(e.message);
    }
    setSaving(false);
  };

  const signInUrl = useMemo(() => {
    if (mode === 'local' || !tenantValid) return null;
    const base = (process.env.REACT_APP_API_URL || window.location.origin).replace(/\/$/, '');
    return `${base}/api/auth/sso/azure/start?org_id=${encodeURIComponent(org.id)}`;
  }, [mode, tenantValid, org.id]);

  const canSendLink = !!signInUrl && !!config?.identity_mode && config.identity_mode !== 'local';

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10 px-4">
      <div className="w-full max-w-3xl bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl">
        <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-white">Identity · {org.name}</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Configure how this organization’s users authenticate. Only you can change this.
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white text-2xl leading-none">×</button>
        </div>

        {loading ? (
          <div className="px-6 py-16 text-center text-slate-400 text-sm">Loading…</div>
        ) : (
          <div className="px-6 py-5 space-y-6">
            {err && (
              <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm text-red-200">{err}</div>
            )}

            {/* Verification badge */}
            {mode !== 'local' && (
              <div className={`rounded-lg border px-4 py-3 text-sm flex items-center justify-between gap-3 ${
                config?.federation_verified_at
                  ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200'
                  : 'border-amber-500/40 bg-amber-500/10 text-amber-200'
              }`}>
                <div>
                  <span className="font-semibold">
                    {config?.federation_verified_at ? 'Verified' : 'Awaiting first sign-in'}
                  </span>
                  <span className="text-xs ml-2 opacity-80">
                    {config?.federation_verified_at
                      ? `since ${new Date(config.federation_verified_at).toLocaleString()}`
                      : 'Marks Verified automatically the first time a user from this organization signs in successfully.'}
                  </span>
                </div>
                {config?.federation_verified_at && (
                  <button onClick={resetVerification} className="text-xs underline opacity-90 hover:opacity-100">
                    Reset
                  </button>
                )}
              </div>
            )}

            {/* Mode selector */}
            <div>
              <label className="text-xs uppercase tracking-wide text-slate-400 font-semibold mb-2 block">Sign-in mode</label>
              <div className="space-y-2">
                {MODE_OPTIONS.map((opt) => (
                  <label key={opt.value} className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition ${
                    mode === opt.value
                      ? 'border-blue-500 bg-blue-500/10'
                      : 'border-slate-700 hover:border-slate-600 bg-slate-800/40'
                  }`}>
                    <input
                      type="radio"
                      name="identity_mode"
                      value={opt.value}
                      checked={mode === opt.value}
                      onChange={() => setMode(opt.value)}
                      className="mt-1"
                    />
                    <div>
                      <div className="text-sm font-medium text-white">{opt.label}</div>
                      <div className="text-xs text-slate-400 mt-0.5">{opt.blurb}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {mode !== 'local' && (
              <>
                {/* Tenant ID */}
                <div>
                  <label className="text-xs uppercase tracking-wide text-slate-400 font-semibold mb-2 block">
                    Microsoft Entra tenant ID
                  </label>
                  <input
                    type="text"
                    value={tenantId}
                    onChange={(e) => setTenantId(e.target.value)}
                    placeholder="00000000-0000-0000-0000-000000000000"
                    className={`w-full px-3 py-2 rounded-lg bg-slate-800 border text-white text-sm font-mono placeholder:text-slate-600 focus:outline-none ${
                      tenantValid ? 'border-slate-700 focus:border-blue-500' : 'border-red-500/60'
                    }`}
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Ask the client’s IT admin for their <span className="font-mono">tenant ID</span> (UUID). They can copy it from <span className="font-mono">portal.azure.com → Microsoft Entra ID → Overview</span>.
                  </p>
                </div>

                {/* Allowed domains */}
                <div>
                  <label className="text-xs uppercase tracking-wide text-slate-400 font-semibold mb-2 block">
                    Verified email domains
                  </label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {domains.map((d) => (
                      <span key={d} className="px-2.5 py-1 rounded-lg bg-slate-700/50 border border-slate-600 text-xs text-white inline-flex items-center gap-2">
                        {d}
                        <button onClick={() => removeDomain(d)} className="text-slate-400 hover:text-red-300 leading-none">×</button>
                      </span>
                    ))}
                    {domains.length === 0 && (
                      <span className="text-xs text-slate-500 italic">No domains yet</span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={domainInput}
                      onChange={(e) => setDomainInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addDomain(); } }}
                      placeholder="clientco.com"
                      className="flex-1 px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm placeholder:text-slate-600 focus:outline-none focus:border-blue-500"
                    />
                    <button
                      onClick={addDomain}
                      type="button"
                      className="px-3 py-2 rounded-lg bg-blue-500/20 border border-blue-500/40 text-blue-200 text-sm hover:bg-blue-500/30"
                    >
                      Add
                    </button>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    A sign-in is only accepted if the user’s email ends with one of these.
                  </p>
                </div>

                {/* JIT */}
                <div className="rounded-xl border border-slate-700 bg-slate-800/40 p-4">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" checked={jitEnabled} onChange={(e) => setJitEnabled(e.target.checked)} className="mt-1" />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-white">Auto-provision new users on first sign-in</div>
                      <div className="text-xs text-slate-400 mt-0.5">
                        If off, every user must be invited by an org admin before they can sign in. If on, any user from a verified domain is created automatically with the role below.
                      </div>
                    </div>
                  </label>
                  {jitEnabled && (
                    <div className="mt-3 ml-7">
                      <label className="text-xs text-slate-400 mb-1 block">Default role for auto-provisioned users</label>
                      <select
                        value={jitRole}
                        onChange={(e) => setJitRole(e.target.value)}
                        className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm focus:outline-none focus:border-blue-500"
                      >
                        {JIT_ROLE_OPTIONS.map((r) => (<option key={r.value} value={r.value}>{r.label}</option>))}
                      </select>
                    </div>
                  )}
                </div>

                {/* Send sign-in link */}
                {canSendLink && (
                  <div className="rounded-xl border border-slate-700 bg-slate-800/40 p-4">
                    <div className="text-sm font-medium text-white mb-1">Email sign-in link to the client</div>
                    <p className="text-xs text-slate-400 mb-3">
                      Sends a branded welcome email with their permanent Microsoft sign-in link. They click the button in the email and they’re in.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
                      <input
                        type="email"
                        value={linkRecipientEmail}
                        onChange={(e) => setLinkRecipientEmail(e.target.value)}
                        placeholder="superuser@clientco.com"
                        className="px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-white text-sm placeholder:text-slate-600 focus:outline-none focus:border-blue-500"
                      />
                      <input
                        type="text"
                        value={linkRecipientName}
                        onChange={(e) => setLinkRecipientName(e.target.value)}
                        placeholder="Recipient name (optional)"
                        className="px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-white text-sm placeholder:text-slate-600 focus:outline-none focus:border-blue-500"
                      />
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={sendSignInLink}
                        disabled={sendingLink || !linkRecipientEmail}
                        type="button"
                        className="px-4 py-2 rounded-lg bg-gradient-to-r from-orange-500 to-amber-500 text-white text-sm font-semibold hover:shadow-lg hover:shadow-blue-500/25 transition disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {sendingLink ? 'Sending…' : 'Send sign-in email'}
                      </button>
                      {linkSentTo && (
                        <span className="text-xs text-emerald-300">Sent to {linkSentTo}</span>
                      )}
                    </div>
                  </div>
                )}
                {!canSendLink && tenantValid && (
                  <div className="rounded-xl border border-slate-700 bg-slate-800/40 p-3 text-xs text-slate-400">
                    Save first to enable the email sign-in link to your client.
                  </div>
                )}
              </>
            )}

            {/* Recent events */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Recent federation events</label>
                <button onClick={fetchEvents} className="text-xs text-blue-400 hover:text-blue-300">Refresh</button>
              </div>
              <div className="rounded-xl border border-slate-700 bg-slate-800/40 max-h-64 overflow-y-auto">
                {eventsLoading ? (
                  <div className="px-4 py-6 text-center text-xs text-slate-500">Loading…</div>
                ) : events.length === 0 ? (
                  <div className="px-4 py-6 text-center text-xs text-slate-500">No events yet</div>
                ) : (
                  <table className="w-full text-xs">
                    <tbody>
                      {events.map((ev) => (
                        <tr key={ev.id} className="border-b border-slate-700/40 last:border-0">
                          <td className="px-3 py-2 text-slate-400 whitespace-nowrap">{new Date(ev.created_at).toLocaleString()}</td>
                          <td className="px-3 py-2">
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                              ev.outcome === 'success' ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30'
                                                       : 'bg-red-500/15 text-red-300 border border-red-500/30'
                            }`}>{ev.event_type}</span>
                          </td>
                          <td className="px-3 py-2 text-slate-300 truncate max-w-[200px]">{ev.email_claim || '—'}</td>
                          <td className="px-3 py-2 text-slate-500 truncate max-w-[140px]">{ev.failure_reason || ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="px-6 py-4 border-t border-slate-700 flex items-center justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-slate-300 hover:text-white">Close</button>
          <button
            onClick={save}
            disabled={!canSave}
            className="px-5 py-2 rounded-lg bg-gradient-to-r from-orange-500 to-amber-500 text-white text-sm font-semibold hover:shadow-lg hover:shadow-blue-500/25 transition disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
